import React, { useState } from 'react';
import { Lock, User, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../supabaseClient';

interface LoginPageProps {
  onAuthenticated: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    try {
      setLoading(true);
      
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        if (signInError.message?.includes('Invalid login credentials')) {
          setError('Invalid login credentials.');
        } else if (signInError.message?.includes('Email not confirmed')) {
          setError('Please confirm your email first, or contact an administrator.');
        } else {
          setError(`Authentication error: ${signInError.message}`);
        }
        return;
      }

      if (data?.session) {
        // Small delay to ensure session is properly set
        setTimeout(() => {
          onAuthenticated();
        }, 100);
      } else {
        setError('Authentication failed. No session created.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 relative overflow-hidden">
      {/* Background mesh */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-80 h-80 rounded-full bg-sky-200/40 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-900 shadow-lg mb-3">
            <div className="w-4 h-4 border-2 border-white rounded-sm transform rotate-45" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
            Ethereal Solutions
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Secure sign-in for internal workspace access.
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl border border-white/60 shadow-xl rounded-2xl p-6 space-y-5">
          <div>
            <h2 className="text-sm font-medium text-slate-800 mb-1">Sign In</h2>
            <p className="text-xs text-slate-500">
              Only authorized users can access this workspace.
            </p>
          </div>

          {error && (
            <div className="flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50/70 px-3 py-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">Email</label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  autoComplete="email"
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/70 border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500/30 focus:border-slate-500/60"
                  placeholder="voortkasumov@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  autoComplete="current-password"
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/70 border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500/30 focus:border-slate-500/60"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium shadow-lg shadow-slate-300/40 hover:bg-slate-800 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;


